import React, { useMemo, useRef, useState } from 'react';
import { addDoc, arrayUnion, collection, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../config/firebase.js';
import logger from '../../utils/logger';
import { useUser } from '../../utils/UserContext';
import { GRADE_NAMES, cleanGrade, subjectsForGrade } from '../../utils/catalogue';
import toPascalCase from '../../utils/toPascalCase';
import { NAMING_HINT, generateTitle, normaliseTitle } from '../../utils/resourceNaming';
import TagInput from './TagInput';
import { isDuplicateTitle, logUploadEvent, uploadResource, validateFile } from './uploadResource';

/**
 * DISC-02 Option B — bulk upload with shared defaults and an editable grid.
 *
 * The uploader sets the grade, subject, tags and contributor shared by the
 * batch once, then completes only what differs per file (usually the theme).
 * Files upload a few at a time with their own progress; a failure does not
 * stop the rest of the batch, and failed files can be retried.
 *
 * Each upload is an ordinary resource record (so it enters the moderation
 * queue and REQ-02 digests) tagged with a batchId. The batch record lets the
 * notification function send one message per moderator for the whole batch.
 */

// Batch limits are to be agreed with NWU before go-live (work order §2)
export const MAX_BATCH_FILES = 20;
const CONCURRENCY = 3;

const cellInput =
  'w-full rounded-md border border-gray-300 px-2 py-1.5 text-xs text-gray-900 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500';
const fieldClass =
  'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-brand-500 focus:border-brand-500 focus:outline-none bg-white';

const STATUS_STYLES = {
  ready: 'text-gray-500',
  invalid: 'text-amber-700',
  uploading: 'text-brand-600',
  done: 'text-emerald-700',
  skipped: 'text-amber-700',
  failed: 'text-red-600',
};

let rowCounter = 0;
const newRow = (file) => ({
  id: `row-${(rowCounter += 1)}`,
  file,
  grade: '', // '' uses the batch default
  subject: '', // '' uses the batch default
  theme: '',
  title: '',
  titleEdited: false,
  status: 'ready',
  progress: 0,
  message: '',
  path: '',
});

const BulkUpload = () => {
  const { currentUser } = useUser();
  const [defaults, setDefaults] = useState({ grade: '', subject: '', contributor: '' });
  const [tags, setTags] = useState([]);
  const [rows, setRows] = useState([]);
  const [running, setRunning] = useState(false);
  const [notice, setNotice] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef(null);

  const updateRow = (id, changes) => setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...changes } : r)));

  // Resolve each row against the batch defaults and validate it
  const resolved = useMemo(() => {
    const list = rows.map((row) => {
      const gradeName = row.grade || defaults.grade;
      const offered = subjectsForGrade(gradeName);
      const subjectName = row.subject || defaults.subject;
      const subjectOk = offered.includes(subjectName);
      const suggested = generateTitle({ gradeName, subjectName: subjectOk ? subjectName : '', theme: row.theme });
      const title = normaliseTitle(row.titleEdited ? row.title : suggested);

      let problem = validateFile(row.file);
      if (!problem && !gradeName) problem = 'Choose a grade.';
      if (!problem && !subjectOk) problem = subjectName ? `${subjectName} is not offered in ${gradeName}.` : 'Choose a subject.';
      if (!problem && !row.theme.trim()) problem = 'Add a theme.';
      if (!problem && !title) problem = 'Add a document name.';
      return { ...row, gradeName, subjectName, offered, suggested, effectiveTitle: title, problem };
    });

    // Duplicates within the batch: same name in the same scope, or same file twice
    const names = new Map();
    const files = new Map();
    list.forEach((r) => {
      const nameKey = `${r.gradeName}/${r.subjectName}/${r.effectiveTitle.toLowerCase()}`;
      const fileKey = `${r.file.name}/${r.file.size}`;
      names.set(nameKey, (names.get(nameKey) || 0) + 1);
      files.set(fileKey, (files.get(fileKey) || 0) + 1);
    });
    return list.map((r) => {
      if (r.problem) return r;
      if (files.get(`${r.file.name}/${r.file.size}`) > 1) return { ...r, problem: 'This file is in the batch more than once.' };
      if (names.get(`${r.gradeName}/${r.subjectName}/${r.effectiveTitle.toLowerCase()}`) > 1) {
        return { ...r, problem: 'Another file in this batch has the same name.' };
      }
      return r;
    });
  }, [rows, defaults]);

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    const pendingRows = rows.filter((r) => r.status !== 'done');
    const room = MAX_BATCH_FILES - pendingRows.length;
    const accepted = incoming.slice(0, Math.max(0, room));
    setNotice(
      incoming.length > accepted.length
        ? `A batch can contain up to ${MAX_BATCH_FILES} files. ${incoming.length - accepted.length} file(s) were not added.`
        : ''
    );
    setRows((prev) => [...prev.filter((r) => r.status !== 'done'), ...accepted.map(newRow)]);
  };

  const processRow = async (row, batchId) => {
    const grade = cleanGrade(row.gradeName);
    const subject = toPascalCase(row.subjectName);
    const item = { title: row.effectiveTitle, grade, subject, fileName: row.file.name };
    updateRow(row.id, { status: 'uploading', progress: 0, message: '' });
    try {
      if (await isDuplicateTitle(grade, subject, row.effectiveTitle)) {
        updateRow(row.id, { status: 'skipped', message: 'A resource with this name already exists for this grade and subject.' });
        return { ...item, status: 'skipped', error: 'duplicate' };
      }
      const path = await uploadResource({
        grade,
        subject,
        title: row.effectiveTitle,
        theme: row.theme,
        tags,
        contributor: defaults.contributor,
        file: row.file,
        batchId,
        onProgress: (progress) => updateRow(row.id, { progress }),
      });
      updateRow(row.id, { status: 'done', progress: 100, path, message: 'Uploaded for moderation.' });
      logUploadEvent({ subject: row.subjectName, title: row.effectiveTitle, contributor: defaults.contributor });
      return { ...item, status: 'done', path };
    } catch (err) {
      logger.error(`Bulk upload failed for ${row.file.name}:`, err);
      const message = err?.code === 'permission-denied' ? 'Permission denied.' : 'Upload failed. You can retry this file.';
      updateRow(row.id, { status: 'failed', message });
      return { ...item, status: 'failed', error: err?.code || 'error' };
    }
  };

  const runBatch = async (toUpload) => {
    setRunning(true);
    setNotice('');
    let batchRef;
    try {
      batchRef = await addDoc(collection(db, 'upload_batches'), {
        userId: currentUser?.uid,
        userEmail: currentUser?.email || null,
        status: 'uploading',
        total: toUpload.length,
        createdAt: Timestamp.now(),
      });
    } catch (err) {
      logger.error('Could not start the batch:', err);
      setNotice('The batch could not be started. Please check your connection and try again.');
      setRunning(false);
      return;
    }

    // Each result is recorded as it finishes, so if this page closes
    // mid-batch the server-side sweep can still complete the batch and
    // notify moderators.
    const results = [];
    let next = 0;
    const worker = async () => {
      while (next < toUpload.length) {
        const row = toUpload[next];
        next += 1;
        const result = await processRow(row, batchRef.id);
        results.push(result);
        await updateDoc(batchRef, { items: arrayUnion(result) }).catch((err) =>
          logger.error('Could not record batch progress:', err)
        );
      }
    };
    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, toUpload.length) }, worker));

    const count = (status) => results.filter((r) => r.status === status).length;
    try {
      await updateDoc(batchRef, {
        status: 'complete',
        completedAt: Timestamp.now(),
        succeeded: count('done'),
        failed: count('failed'),
        skipped: count('skipped'),
      });
    } catch (err) {
      logger.error('Could not record batch completion:', err);
    }
    setRunning(false);
  };

  const startUpload = () => {
    setAttempted(true);
    const candidates = resolved.filter((r) => r.status === 'ready' || r.status === 'invalid');
    const invalid = candidates.filter((r) => r.problem);
    if (candidates.length === 0) return;
    if (invalid.length) {
      setNotice(`${invalid.length} file(s) need attention before the batch can be uploaded.`);
      return;
    }
    if (tags.length === 0) {
      setNotice('Add at least one tag for the batch.');
      return;
    }
    runBatch(candidates);
  };

  const retryFailed = () => {
    const failed = resolved.filter((r) => r.status === 'failed' && !r.problem);
    if (failed.length) runBatch(failed);
  };

  const startNewBatch = () => {
    setRows([]);
    setNotice('');
    setAttempted(false);
  };

  const counts = resolved.reduce((acc, r) => ({ ...acc, [r.status]: (acc[r.status] || 0) + 1 }), {});
  const finished = !running && rows.length > 0 && resolved.every((r) => ['done', 'failed', 'skipped'].includes(r.status));
  const editable = (r) => !running && (r.status === 'ready' || r.status === 'invalid' || r.status === 'failed');
  const defaultSubjects = subjectsForGrade(defaults.grade);

  return (
    <div className="p-6 space-y-6">
      {/* Shared defaults */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900">1. Details shared by the batch</h2>
        <p className="text-xs text-gray-500 mt-0.5">Set these once. You can override grade and subject for individual files in the grid.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Default grade</label>
            <select
              value={defaults.grade}
              disabled={running}
              onChange={(e) => setDefaults((d) => ({ ...d, grade: e.target.value, subject: subjectsForGrade(e.target.value).includes(d.subject) ? d.subject : '' }))}
              className={fieldClass}
            >
              <option value="">Select grade</option>
              {GRADE_NAMES.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Default subject</label>
            <select
              value={defaults.subject}
              disabled={running || !defaults.grade}
              onChange={(e) => setDefaults((d) => ({ ...d, subject: e.target.value }))}
              className={`${fieldClass} ${!defaults.grade ? 'opacity-50' : ''}`}
            >
              <option value="">Select subject</option>
              {defaultSubjects.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Contributor name <span className="text-gray-400">(optional)</span></label>
            <input
              value={defaults.contributor}
              disabled={running}
              onChange={(e) => setDefaults((d) => ({ ...d, contributor: e.target.value }))}
              placeholder="Anonymous"
              className={fieldClass}
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Tags for every file</label>
          <TagInput tags={tags} onChange={setTags} error={attempted && tags.length === 0} />
        </div>
      </section>

      {/* File selection */}
      <section>
        <h2 className="text-sm font-semibold text-gray-900">2. Files</h2>
        <label
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (!running) addFiles(e.dataTransfer.files); }}
          className={`mt-2 flex flex-col items-center justify-center w-full h-28 rounded-lg border-2 border-dashed transition-colors ${
            running ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-100 hover:border-gray-400'
          } ${dragging ? 'border-brand-500 bg-brand-50' : 'border-gray-300 bg-gray-50'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400 mb-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
          <p className="text-sm text-gray-500"><span className="font-medium text-brand-600">Choose files</span> or drag them here</p>
          <p className="text-xs text-gray-400 mt-0.5">Up to {MAX_BATCH_FILES} files · PDF, DOC, DOCX, PPT, PPTX · Max 10 MB each</p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            disabled={running}
            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            className="hidden"
          />
        </label>
      </section>

      {/* Editable grid */}
      {rows.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-900">3. Check each file</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            Add a theme for each file. Names follow the convention {NAMING_HINT}; edit any name you want to change.
          </p>
          <div className="mt-3 overflow-x-auto rounded-xl border border-gray-200">
            <table className="w-full min-w-[960px] text-left text-xs">
              <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-3 py-2 font-semibold w-[18%]">File</th>
                  <th className="px-3 py-2 font-semibold w-[11%]">Grade</th>
                  <th className="px-3 py-2 font-semibold w-[15%]">Subject</th>
                  <th className="px-3 py-2 font-semibold w-[15%]">Theme</th>
                  <th className="px-3 py-2 font-semibold w-[23%]">Document name</th>
                  <th className="px-3 py-2 font-semibold w-[15%]">Status</th>
                  <th className="px-3 py-2 w-[3%]"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {resolved.map((r) => {
                  const canEdit = editable(r);
                  const showProblem = r.problem && (attempted || r.status === 'invalid') && canEdit;
                  return (
                    <tr key={r.id} className={showProblem ? 'bg-amber-50/50' : ''}>
                      <td className="px-3 py-2 align-top">
                        <div className="font-medium text-gray-800 break-all">{r.file.name}</div>
                        <div className="text-gray-400">{(r.file.size / 1024 / 1024).toFixed(1)} MB</div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <select
                          value={r.grade}
                          disabled={!canEdit}
                          onChange={(e) => updateRow(r.id, { grade: e.target.value })}
                          className={cellInput}
                        >
                          <option value="">{defaults.grade ? `Default (${defaults.grade})` : 'Select'}</option>
                          {GRADE_NAMES.map((g) => <option key={g} value={g}>{g}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <select
                          value={r.subject}
                          disabled={!canEdit || !r.gradeName}
                          onChange={(e) => updateRow(r.id, { subject: e.target.value })}
                          className={cellInput}
                        >
                          <option value="">{defaults.subject ? `Default (${defaults.subject})` : 'Select'}</option>
                          {r.offered.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          value={r.theme}
                          disabled={!canEdit}
                          onChange={(e) => updateRow(r.id, { theme: e.target.value })}
                          placeholder="e.g. Fractions"
                          className={cellInput}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <input
                          value={r.titleEdited ? r.title : r.suggested}
                          disabled={!canEdit}
                          onChange={(e) => updateRow(r.id, { title: e.target.value, titleEdited: true })}
                          className={cellInput}
                        />
                        {r.titleEdited && canEdit && (
                          <button type="button" onClick={() => updateRow(r.id, { titleEdited: false, title: '' })} className="mt-1 text-[11px] text-brand-600 hover:text-brand-700">
                            Use suggested name
                          </button>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {r.status === 'uploading' ? (
                          <div>
                            <div className="flex justify-between text-brand-600"><span>Uploading</span><span>{Math.round(r.progress)}%</span></div>
                            <div className="mt-1 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
                              <div className="h-full bg-brand-600 transition-all" style={{ width: `${r.progress}%` }} />
                            </div>
                          </div>
                        ) : showProblem ? (
                          <span className={STATUS_STYLES.invalid}>{r.problem}</span>
                        ) : (
                          <span className={STATUS_STYLES[r.status]}>
                            {r.status === 'ready' ? 'Ready' : r.message || r.status}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        {canEdit && (
                          <button
                            type="button"
                            onClick={() => setRows((prev) => prev.filter((x) => x.id !== r.id))}
                            className="text-gray-400 hover:text-red-500"
                            aria-label={`Remove ${r.file.name}`}
                          >
                            ✕
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {notice && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">{notice}</div>
      )}

      {finished && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          <p className="font-medium">Batch finished</p>
          <p className="mt-0.5">
            {counts.done || 0} uploaded for moderation
            {counts.failed ? ` · ${counts.failed} failed` : ''}
            {counts.skipped ? ` · ${counts.skipped} skipped as duplicates` : ''}.
          </p>
          {(counts.failed || counts.skipped) ? (
            <ul className="mt-2 list-disc pl-5 text-xs text-gray-600 space-y-0.5">
              {resolved.filter((r) => r.status === 'failed' || r.status === 'skipped').map((r) => (
                <li key={r.id}><span className="font-medium">{r.file.name}</span>: {r.message}</li>
              ))}
            </ul>
          ) : null}
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-wrap gap-3">
          {!finished && (
            <button
              type="button"
              onClick={startUpload}
              disabled={running}
              className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 disabled:bg-brand-400 disabled:cursor-not-allowed"
            >
              {running ? `Uploading… (${counts.done || 0} of ${rows.length} done)` : `Upload ${resolved.filter((r) => r.status === 'ready' || r.status === 'invalid').length} file(s)`}
            </button>
          )}
          {finished && counts.failed > 0 && (
            <button type="button" onClick={retryFailed} className="px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-brand-600 hover:bg-brand-700">
              Retry {counts.failed} failed file(s)
            </button>
          )}
          {finished && (
            <button type="button" onClick={startNewBatch} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50">
              Start a new batch
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkUpload;
